// Centralized lazy import factories so we can both lazy() them in the router
// AND fire them on hover/focus to prefetch chunks before navigation.
//
// Usage:
//   import { prefetchRoute, routeLoaders } from '@/lib/routePrefetch';
//   <Link onMouseEnter={() => prefetchRoute('/events')} ... />

export const routeLoaders: Record<string, () => Promise<unknown>> = {
  '/': () => import('@/pages/Index'),
  '/auth': () => import('@/pages/Auth'),
  '/reset-password': () => import('@/pages/ResetPassword'),
  '/dashboard': () => import('@/pages/Dashboard'),
  '/shop': () => import('@/pages/Shop'),
  '/apparel': () => import('@/pages/Apparel'),
  '/checkout': () => import('@/pages/Checkout'),
  '/events': () => import('@/pages/Events'),
  '/event-albums': () => import('@/pages/EventAlbums'),
  '/blog': () => import('@/pages/Blog'),
  '/wishlist': () => import('@/pages/Wishlist'),
  '/rewards': () => import('@/pages/Rewards'),
  '/account': () => import('@/pages/Account'),
  '/account/orders': () => import('@/pages/Account'),
  '/ambassador': () => import('@/pages/Ambassador'),
  '/admin': () => import('@/pages/Admin'),
  '/admin/products': () => import('@/pages/admin/AdminProducts'),
  '/admin/coupons': () => import('@/pages/admin/AdminCoupons'),
  '/admin/ambassadors': () => import('@/pages/admin/AdminAmbassadors'),
  '/admin/sub-admins': () => import('@/pages/admin/AdminSubAdmins'),
  '/my-referrals': () => import('@/pages/MyReferrals'),
  '/faq': () => import('@/pages/FAQ'),
  '/size-guide': () => import('@/pages/SizeGuide'),
  '/about': () => import('@/pages/About'),
  '/partners': () => import('@/pages/PartnersSponsors'),
  '/policies/legal': () => import('@/pages/policies/LegalPolicies'),
  '/policies/no-refund': () => import('@/pages/policies/NoRefundPolicy'),
  '/policies/license-plate-disclaimer': () => import('@/pages/policies/LicensePlateDisclaimer'),
  '/policies/delivery': () => import('@/pages/policies/DeliveryPolicy'),
  '/policies/privacy': () => import('@/pages/policies/PrivacyPolicy'),
  '/policies/terms': () => import('@/pages/policies/TermsOfService'),
  '/policies/domain-communication': () => import('@/pages/policies/DomainCommunicationPolicy'),
};

const prefetched = new Set<string>();

export function prefetchRoute(path: string): void {
  // Strip query/hash and trailing slash for matching.
  const clean = path.split('?')[0].split('#')[0].replace(/\/$/, '') || '/';
  if (prefetched.has(clean)) return;
  const loader = routeLoaders[clean];
  if (!loader) return;
  prefetched.add(clean);
  // Fire and forget; ignore errors (network, etc.)
  loader().catch(() => prefetched.delete(clean));
}

/** Spread these onto a Link/button to prefetch the target chunk on intent. */
export function prefetchHandlers(path: string) {
  return {
    onMouseEnter: () => prefetchRoute(path),
    onFocus: () => prefetchRoute(path),
    onTouchStart: () => prefetchRoute(path),
  };
}
